<template>
  <div class="composer-card">
    <div class="composer-input-row" @click="onPrimaryAdd">
      <a-avatar :size="40" v-if="logo" :src="logo" />
      <a-avatar :size="40" v-else icon="user" />
      <div class="composer-fake-input">
        <transition name="composer-prompt" mode="out-in">
          <span :key="composerPromptIndex" class="composer-prompt-text">
            {{ currentComposerPrompt }}
          </span>
        </transition>
      </div>
    </div>
    <div class="composer-divider"></div>
    <div class="composer-actions-row">
      <template v-if="mode === 'club'">
        <button class="composer-action-btn" @click="$emit('add')">
          <span class="composer-icon blue"><a-icon type="plus" /></span>
          {{ $t("activityClub.addPost") }}
        </button>
      </template>
      <template v-else>
        <button class="composer-action-btn" @click="$emit('add-news')">
          <span class="composer-icon blue"><a-icon type="file-text" /></span>
          {{ $t("actualite.ajouterActualite") }}
        </button>
        <button class="composer-action-btn" @click="$emit('add-album')">
          <span class="composer-icon green"><a-icon type="camera" /></span>
          {{ $t("actualite.ajouterAlbum") }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "ActualityComposer",
  props: {
    logo: { type: String, default: null },
    mode: { type: String, default: "news" }, // news | club
  },
  data() {
    return {
      composerPromptIndex: 0,
      _composerPromptTimer: null,
    };
  },
  computed: {
    composerPromptKeys() {
      if (this.mode === "club") {
        return [
          "activityClub.composerPrompt1",
          "activityClub.composerPrompt2",
          "activityClub.composerPrompt3",
        ];
      }
      return [
        "actualite.composerPrompt1",
        "actualite.composerPrompt2",
        "actualite.composerPrompt3",
        "actualite.composerPrompt4",
        "actualite.composerPrompt5",
      ];
    },
    currentComposerPrompt() {
      const key = this.composerPromptKeys[this.composerPromptIndex % this.composerPromptKeys.length];
      return this.$te(key) ? this.$t(key) : this.$t("activityClub.addPost");
    },
  },
  mounted() {
    this.startComposerPromptRotation();
  },
  beforeDestroy() {
    this.stopComposerPromptRotation();
  },
  methods: {
    onPrimaryAdd() {
      if (this.mode === "club") this.$emit("add");
      else this.$emit("add-news");
    },
    startComposerPromptRotation() {
      this.stopComposerPromptRotation();
      this._composerPromptTimer = setInterval(() => {
        this.composerPromptIndex =
          (this.composerPromptIndex + 1) % this.composerPromptKeys.length;
      }, 5000);
    },
    stopComposerPromptRotation() {
      if (this._composerPromptTimer) {
        clearInterval(this._composerPromptTimer);
        this._composerPromptTimer = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
$fb-blue: #4b7cf3;
$fb-bg: #f0f2f5;
$fb-muted: #65676b;

@mixin feed-accent-card {
  background: linear-gradient(135deg, #f0f5ff 0%, #f8faff 50%, #eef4ff 100%);
  border: 1px solid #c5d8f8;
  border-radius: 12px;
  box-shadow: 0 1px 1px rgba(75, 124, 243, 0.05);
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: #a8c4f5;
  }
}

.composer-card {
  max-width: none;
  margin: 0 0 16px;
  padding: 14px 18px;
  @include feed-accent-card;

  :deep(.anticon) {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
}

.composer-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.composer-fake-input {
  flex: 1;
  background: transparent;
  border: 1px dashed rgba(75, 124, 243, 0.28);
  border-radius: 22px;
  padding: 10px 16px;
  color: $fb-muted;
  font-size: 15px;
  line-height: 1.4;
  min-height: 42px;
  display: flex;
  align-items: center;
  transition: border-color 0.15s ease, background 0.15s ease;
  overflow: hidden;
}

.composer-prompt-text {
  display: block;
  font-style: italic;
  color: #8a8d91;
}

.composer-prompt-enter-active,
.composer-prompt-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.composer-prompt-enter {
  opacity: 0;
  transform: translateY(6px);
}

.composer-prompt-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.composer-input-row:hover .composer-fake-input {
  background: rgba(255, 255, 255, 0.22);
  border-color: rgba(75, 124, 243, 0.45);
  border-style: solid;
}

.composer-divider {
  height: 1px;
  background: rgba(75, 124, 243, 0.12);
  margin: 12px 0;
}

.composer-actions-row {
  display: flex;
  gap: 8px;
}

.composer-action-btn {
  flex: 1;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 6px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.25;
  color: $fb-muted;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: rgba(75, 124, 243, 0.1);
    color: $fb-blue;
    transform: translateY(-1px);
  }
}

.composer-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 14px;
  line-height: 1;
  color: #fff;
  flex-shrink: 0;

  :deep(.anticon) {
    font-size: 14px;
  }

  &.blue {
    background: $fb-blue;
  }
  &.green {
    background: #45bd62;
  }
}
</style>
